import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { LINKS } from '../lib/links'
import { useT, useLocale } from '../i18n/LocaleContext'
import { localizedPath } from '../i18n/routes'

// A distinct minimal line icon per trust item (Picfast-style, not a generic check).
const TRUST_ICONS: Record<string, ReactNode> = {
  trustDelivery: (
    <>
      <path d="M3 7a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v9H3z" />
      <path d="M15 9h3.5a1 1 0 0 1 .82.43l1.5 2.14a1 1 0 0 1 .18.57V16h-6z" />
      <circle cx="7" cy="17.5" r="1.5" />
      <circle cx="17" cy="17.5" r="1.5" />
    </>
  ),
  trustAllLevels: (
    <>
      <path d="M16 19v-1a3 3 0 0 0-3-3H6a3 3 0 0 0-3 3v1" />
      <circle cx="9.5" cy="8" r="3" />
      <path d="M21 19v-1a3 3 0 0 0-2.25-2.9" />
      <path d="M15.5 5.2a3 3 0 0 1 0 5.6" />
    </>
  ),
  trustNoCommitment: (
    <>
      <rect x="4" y="10.5" width="16" height="9.5" rx="2" />
      <path d="M8 10.5V7a4 4 0 0 1 7.5-1.9" />
      <circle cx="12" cy="15" r="1.2" />
    </>
  ),
  trustReturns: (
    <>
      <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
      <path d="M21 4v4h-4" />
      <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
      <path d="M3 20v-4h4" />
    </>
  ),
}

type Segment = {
  text: string
  className?: string
  gradient?: boolean
}

/**
 * Renders a headline line with optional gradient/italic segments.
 * Pure CSS animation (no framer-motion) — H1 is the LCP element, must paint
 * immediately on SSG without waiting for JS hydration to release opacity.
 */
function RevealLine({
  segments,
  delayClass = 'hero-delay-line-1',
}: {
  segments: Segment[]
  delayClass?: string
}) {
  return (
    <span className={`inline-block hero-line-reveal ${delayClass}`}>
      {segments.map((seg, i) => (
        <span key={i}>
          <span
            className={
              seg.gradient
                ? `${seg.className ?? ''} inline-block align-baseline bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.14em] pr-[0.04em]`
                : (seg.className ?? '')
            }
          >
            {seg.text}
          </span>
          {i < segments.length - 1 ? ' ' : ''}
        </span>
      ))}
    </span>
  )
}

export default function Hero() {
  const { t } = useT()
  const locale = useLocale()
  return (
    <section className="relative pt-28 sm:pt-44 pb-20 sm:pb-24">
      <div className="relative z-10 max-w-4xl mx-auto px-5 sm:px-6">
        {/* Badge */}
        <div className="flex justify-center hero-fade-up">
          <div className="inline-flex items-center gap-2 rounded-full border border-accent-200 bg-accent-50/80 backdrop-blur-sm px-3 py-1 text-xs text-accent-800">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-60 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-600" />
            </span>
            <span className="font-medium text-accent-800">{t('hero.badgeAutopilot')}</span>
            <span className="text-accent-400">·</span>
            <span className="text-accent-700">{t('hero.badgeWarehouse')}</span>
          </div>
        </div>

        {/* Headline — content always in DOM for SEO/LCP, CSS-only animation */}
        <h1 className="hero-headline-safe mt-10 sm:mt-12 text-center text-balance font-semibold tracking-[-0.02em] text-ink-950 text-[clamp(1.85rem,4.8vw,3.35rem)] leading-[1.14]">
          <RevealLine
            segments={[
              { text: t('hero.headlineLine1Part1') },
              { text: t('hero.headlineLine1Part2'), gradient: true, className: 'font-semibold font-normal' },
              { text: t('hero.headlineLine1Part3'), className: 'whitespace-nowrap' },
            ]}
            delayClass="hero-delay-line-1"
          />
          <br />
          <RevealLine
            segments={[
              { text: t('hero.headlineLine2Part1'), gradient: true, className: 'font-semibold font-normal' },
              { text: t('hero.headlineLine2Part2') },
              { text: t('hero.headlineLine2Part3'), gradient: true, className: 'font-semibold font-normal' },
            ]}
            delayClass="hero-delay-line-2"
          />
        </h1>

        {/* Subhead */}
        <p className="hero-subhead-safe mt-7 sm:mt-8 text-center text-ink-600 max-w-xl mx-auto text-[0.9rem] sm:text-[1.0125rem] leading-relaxed px-2 sm:px-0 hero-fade-up hero-delay-2">
          {t('hero.subhead')}
        </p>

        {/* CTA — primary (devis) + secondary (calculateur /tarifs) + reassurance */}
        <div className="mt-11 sm:mt-12 flex flex-col items-center hero-fade-up hero-delay-3">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            {/* Shimmer primary CTA — dark blue */}
            <Link
              to={`${localizedPath('contact', locale)}#rendez-vous`}
              className="relative group inline-flex items-center gap-2 bg-accent-700 hover:bg-accent-800 text-white rounded-full pl-6 pr-4 py-3.5 text-sm font-medium shadow-[0_16px_50px_-12px_rgba(59,93,255,0.65)] transition-[background-color,transform] hover:-translate-y-px active:scale-[0.98] overflow-hidden"
            >
              {/* shimmer sweep */}
              <span
                aria-hidden
                className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-[1100ms] ease-out bg-gradient-to-r from-transparent via-white/30 to-transparent pointer-events-none"
              />
              <span className="relative">{t('hero.ctaPrimary')}</span>
              <span className="relative flex items-center justify-center w-5 h-5 rounded-full bg-white/20">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </Link>

            {/* Secondary CTA — même matière que les pills de confiance, mène au
                calculateur : /tarifs n'était joignable que par la nav. */}
            <Link
              to={localizedPath('pricing', locale)}
              className="inline-flex items-center rounded-full border border-[#e8eaf0] bg-white/85 backdrop-blur-sm px-6 py-3.5 text-sm font-medium text-ink-900 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_2px_8px_rgba(16,24,40,0.06)] transition-[border-color,transform] hover:border-ink-300 hover:-translate-y-px active:scale-[0.98]"
            >
              {t('hero.ctaSecondary')}
            </Link>
          </div>

          {/* Reassurance subline */}
          <p className="mt-4 text-xs font-medium text-ink-900">{t('hero.ctaReassurance')}</p>
        </div>

        {/* Trust row — translucent pills, each with its own icon (Picfast-style).
            lg : une seule ligne (le débordement de ~30px du conteneur 4xl créait
            un 4e pill orphelin) — on élargit la rangée au-delà de la colonne. */}
        <div className="mt-12 sm:mt-14 flex flex-wrap lg:flex-nowrap lg:-mx-20 items-center justify-center gap-2.5 text-xs text-ink-700 hero-fade hero-delay-4">
          {(
            ['trustDelivery', 'trustAllLevels', 'trustNoCommitment', 'trustReturns'] as const
          ).map((key) => (
            <span
              key={key}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-[#e8eaf0] bg-white px-3.5 py-2 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_2px_8px_rgba(16,24,40,0.06)]"
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="shrink-0 text-accent-500"
              >
                {TRUST_ICONS[key]}
              </svg>
              {t(`hero.${key}`)}
            </span>
          ))}
        </div>

      </div>
    </section>
  )
}
